import React, { useEffect, useRef, useState } from "react";
import Button from "@mui/material/Button";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Grow from "@mui/material/Grow";
import Popper from "@mui/material/Popper";
import Flex from "src/components/flex";
import Auth from "src/services/auth";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Box, Grid, Stack } from "@mui/material";
import { Link as RouteLink } from "react-router-dom";
import { useAuth } from "../providers/auth-context";
import Subscription from "./subscription";
import AvatarBadge from "../avatar-badge";

const ExpandMore = styled(ExpandMoreIcon)`
    transition: all 0.1s ease-in-out;
    ${({ open }) => (open ? `transform: rotate(-180deg);` : `transform: rotate(0deg);`)}
`;

const MenuBoundingBox = styled.div`
    width: 290px;
    height: 626px;
    left: 1565px;
    top: 111px;
    border: 4px solid #3d3d3d;
    box-sizing: border-box;
    border-radius: 14px;
    background-color: ${({ theme }) => theme.palette.background.default};
`;

const Name = styled.p`
    font-size: 18px;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: ${({ theme }) => theme.palette.text.secondary};
    margin: 0;
`;

const Title = styled.p`
    font-size: 14px;
    font-weight: 400;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: ${({ theme }) => theme.palette.primary.main};
    margin: 0;
`;

const Email = styled.p`
    font-size: 14px;
    font-weight: 200;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: ${({ theme }) => theme.palette.text.primary};
    margin: 0;
`;

const ProfileButton = styled(Button)`
    color: ${({ theme }) => theme.palette.text.secondary};
    text-transform: none;
    border-radius: 24px;
    border: 1px solid ${({ theme }) => theme.palette.text.secondary};
    font-weight: 300;
`;

const SignOutButton = styled(Button)`
    border-radius: 0px 0px 10px 10px;
`;

const UserMenu = ({ isDisemboweled }) => {
    // eslint-disable-next-line no-unused-vars
    const { t } = useTranslation();
    const { user, isLoading } = useAuth();

    const [open, setOpen] = useState(false);
    const anchorRef = useRef(null);

    const renderUserName = () => {
        if (!user) return null;
        if (user && user.name && (user.name.familyName || user.name.givenName))
            return (
                <Name>
                    {user.name.givenName}
                    {` ${user.name.familyName}`}
                </Name>
            );
        if (user && user.displayName) return <Name>{user.displayName}</Name>;
        return null;
    };

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }

        setOpen(false);
    };

    useEffect(() => {
        if (open === true) {
            anchorRef.current.blur();
        }
    }, [open]);

    // TODO find a more elegant way for the image refetch logic
    return (
        <Flex data-testid="header-user-menu">
            <div>
                <Button
                    ref={anchorRef}
                    aria-controls={open ? "menu-list-grow" : undefined}
                    aria-haspopup="true"
                    onClick={handleToggle}
                >
                    <AvatarBadge
                        name={user && user.displayName}
                        picture={user && user.picture}
                        isLoading={isLoading}
                    />
                    <ExpandMore open={open} />
                </Button>

                <Popper
                    open={open}
                    anchorEl={anchorRef.current}
                    role={undefined}
                    style={{ zIndex: 3, wordBreak: "break-all" }}
                    transition
                    disablePortal
                    placement="bottom-end"
                >
                    {({ TransitionProps }) => (
                        <Grow {...TransitionProps} style={{ transformOrigin: "right top" }}>
                            <MenuBoundingBox>
                                <ClickAwayListener onClickAway={handleClose}>
                                    <Grid container direction="column" style={{ height: "100%" }}>
                                        <Grid
                                            item
                                            xs={10}
                                            style={{
                                                borderBottom: "4px solid #3d3d3d",
                                                width: "100%",
                                            }}
                                        >
                                            <Box p={3}>
                                                <Stack spacing={3}>
                                                    <Stack>
                                                        <Stack>
                                                            {renderUserName()}
                                                            <Title>{user?.jobTitle}</Title>
                                                            <Email>{user?.emails?.[0]}</Email>
                                                        </Stack>
                                                        <Subscription plans={user.plans} />
                                                    </Stack>
                                                    {!isDisemboweled && (
                                                        <ProfileButton
                                                            variant="outlined"
                                                            component={RouteLink}
                                                            to="/profile"
                                                            onClick={handleClose}
                                                        >
                                                            Account Settings
                                                        </ProfileButton>
                                                    )}
                                                    {!isDisemboweled && (
                                                        <ProfileButton
                                                            variant="outlined"
                                                            component={RouteLink}
                                                            to="/indexes-settings"
                                                            onClick={handleClose}
                                                        >
                                                            My Indexes Settings
                                                        </ProfileButton>
                                                    )}
                                                    {!isDisemboweled && (
                                                        <ProfileButton
                                                            variant="outlined"
                                                            component={RouteLink}
                                                            to="/personal-profile"
                                                            onClick={handleClose}
                                                        >
                                                            My Profile
                                                        </ProfileButton>
                                                    )}
                                                    {!isDisemboweled && (
                                                        <ProfileButton
                                                            variant="outlined"
                                                            component={RouteLink}
                                                            to="/achievements"
                                                            onClick={handleClose}
                                                        >
                                                            Achievements
                                                        </ProfileButton>
                                                    )}
                                                    {!isDisemboweled && (
                                                        <ProfileButton
                                                            variant="outlined"
                                                            component={RouteLink}
                                                            to="/developer-portal"
                                                            onClick={handleClose}
                                                        >
                                                            Developer Portal
                                                        </ProfileButton>
                                                    )}
                                                    {!isDisemboweled && (
                                                        <ProfileButton
                                                            variant="outlined"
                                                            component={RouteLink}
                                                            to="/subscription"
                                                            onClick={handleClose}
                                                        >
                                                            Manage Plans
                                                        </ProfileButton>
                                                    )}
                                                </Stack>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={2}>
                                            <SignOutButton
                                                onClick={(e) => {
                                                    Auth.logout(false, false);
                                                    handleClose(e);
                                                }}
                                                style={{
                                                    width: "100%",
                                                    height: "100%",
                                                }}
                                                color="secondary"
                                            >
                                                Sign out
                                            </SignOutButton>
                                        </Grid>
                                    </Grid>
                                </ClickAwayListener>
                            </MenuBoundingBox>
                        </Grow>
                    )}
                </Popper>
            </div>
        </Flex>
    );
};

export default UserMenu;
